"use client";

import React, { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import Link from "next/link";

export interface KeywordLink {
  target: string;
  link: string;
  wordBoundary?: boolean;
}

const escapeRegex = (string: string) => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
};

const autoLinkContent = (
  text: string,
  keywords?: KeywordLink[]
): string => {
  if (!text || !keywords || keywords.length === 0) return text;
  let result = text;

  keywords.forEach(({ target, link, wordBoundary }) => {
    // If the link is already in markdown format in the text, skip
    if (result.includes(`(${link})`)) return;

    const escaped = escapeRegex(target);
    const regexStr = wordBoundary
      ? `(?<!\\[)\\b(${escaped})\\b(?!\\]|\\))`
      : `(?<!\\[)(${escaped})(?!\\]|\\))`;

    // Only match first single occurrence without 'g' flag
    const regex = new RegExp(regexStr, "i");
    result = result.replace(regex, `[$1](${link})`);
  });

  return result;
};

interface MarkdownTextProps {
  content?: string | null;
  className?: string;
  linkClassName?: string;
  inline?: boolean;
  keywords?: KeywordLink[];
  disableAutoLinks?: boolean;
}

export const MarkdownText: React.FC<MarkdownTextProps> = ({
  content,
  className = "",
  linkClassName = "text-[#1868F8] hover:text-[#0F52BA] font-semibold underline transition-colors cursor-pointer",
  inline = false,
  keywords,
  disableAutoLinks = false,
}) => {
  if (!content) return null;

  const processedContent = useMemo(() => {
    if (disableAutoLinks || !keywords || keywords.length === 0) return content;
    return autoLinkContent(content, keywords);
  }, [content, keywords, disableAutoLinks]);

  return (
    <div className={`markdown-content ${className}`}>
      <ReactMarkdown
        components={{
          p: ({ node, children, ...props }) => {
            if (inline) {
              return <span {...props}>{children}</span>;
            }
            return (
              <p className="leading-relaxed mb-2 last:mb-0" {...props}>
                {children}
              </p>
            );
          },
          a: ({ node, href, children, ...props }) => {
            if (!href) return <span {...props}>{children}</span>;

            // Convert absolute edvia.ai links to relative paths for fast client routing
            let normalizedHref = href;
            if (
              href.startsWith("https://www.edvia.ai") ||
              href.startsWith("http://www.edvia.ai") ||
              href.startsWith("https://edvia.ai") ||
              href.startsWith("http://edvia.ai")
            ) {
              normalizedHref =
                href.replace(/^https?:\/\/(www\.)?edvia\.ai/, "") || "/";
            }

            const isInternal =
              normalizedHref.startsWith("/") || normalizedHref.startsWith("#");

            if (isInternal) {
              return (
                <Link
                  href={normalizedHref}
                  className={linkClassName}
                  onClick={(e) => e.stopPropagation()}
                >
                  {children}
                </Link>
              );
            }

            return (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className={linkClassName}
                onClick={(e) => e.stopPropagation()}
              >
                {children}
              </a>
            );
          },
        }}
      >
        {processedContent}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownText;
